import styled, { css } from 'styled-components';

export type weight = '400' | '600' | '700' | '900';

export enum TypographyTypes {
    display = 'display',
    headline = 'headline',
    paragraph = 'paragraph',
    label = 'label',
}

type WrapperProps = {
    align: 'inherit' | 'left' | 'center' | 'right' | 'justify';
    color: string;
    gutterBottom: boolean;
    faded: boolean;
    showEllipsis: boolean;
    size: number;
    type: 'display' | 'headline' | 'paragraph' | 'label';
    weight?: weight;
    vertical?: boolean;
    applyFontStylingOnInnerEl?: boolean;
};

const lineHeight = (props: WrapperProps): string => {
    // Display
    if (props.type === TypographyTypes.display) {
        return '80%';
    }

    // Headline
    else if (props.type === TypographyTypes.headline) {
        switch (props.size) {
            case 20:
            case 24:
                return '100%';
            case 30:
                return '90%';
            case 36:
            case 60:
                return '80%';
            case 40:
                return '85%';
        }
    }

    // Paragraph
    else if (props.type === TypographyTypes.paragraph) {
        switch (props.size) {
            case 12:
                return '130%';
            case 14:
            case 16:
                return '120%';
            case 18:
            case 20:
                return '110%';
            case 30:
                return '100%';
        }
    }

    // Label
    else if (props.type === TypographyTypes.label) {
        return '100%';
    }

    // Default
    return '100%';
};

const fontWeight = (props: WrapperProps): string => {
    // Custom weights support
    if (props.weight) return props.weight;

    // Display
    if (props.type === TypographyTypes.display) {
        return '900';
    }

    // Headline
    else if (props.type === TypographyTypes.headline) {
        return 'bold';
    }

    // Paragraph
    else if (props.type === TypographyTypes.paragraph) {
        switch (props.size) {
            case 12:
            case 14:
            case 15:
            case 16:
            case 18:
            case 20:
            case 24:
                return '400';
            case 30:
                return 'bold';
        }
    }

    // Label
    else if (props.type === TypographyTypes.label) {
        return '600';
    }

    // Default
    return 'bold';
};

const letterSpacing = (props: WrapperProps): string => {
    // Display
    if (props.type === TypographyTypes.display) {
        switch (props.size) {
            case 120:
                return '-0.07em';
            case 160:
                return '-0.08em';
        }
    }

    // Headline
    else if (props.type === TypographyTypes.headline) {
        switch (props.size) {
            case 20:
            case 24:
                return '-0.02em';
            case 30:
                return '-0.03em';
            case 36:
            case 60:
                return '-0.05em';
            case 40:
                return '-0.04em';
        }
    }

    // Paragraph
    else if (props.type === TypographyTypes.paragraph) {
        switch (props.size) {
            case 12:
                return '-0.02em';
            case 14:
            case 16:
            case 18:
            case 20:
            case 30:
                return '-0.03em';
        }
    }

    // Label
    else if (props.type === TypographyTypes.label) {
        return '-0.02em';
    }

    // Default
    return '-0.02em';
};

const gutterBottomSpacing = (props: WrapperProps): string | null => {
    if (props.gutterBottom) {
        let marginBottom = 38;
        switch (props.type) {
            // Display
            case TypographyTypes.display:
                marginBottom = 60;
                break;

            // Headline
            case TypographyTypes.headline:
                switch (props.size) {
                    case 20:
                    case 24:
                        marginBottom = 38;
                        break;
                    case 30:
                    case 36:
                    case 60:
                    case 40:
                        marginBottom = 48;
                        break;
                }
                break;

            // Paragraph
            case TypographyTypes.paragraph:
                marginBottom = 38;
                break;
        }
        return `margin-bottom: ${marginBottom}px;`;
    }
    return null;
};

const fadedText = (props: WrapperProps): string | null => (props.faded ? `opacity: .5;` : null);

const showEllipsis = (props: WrapperProps): string | null =>
    props.showEllipsis
        ? `
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `
        : null;

const textTransform = (props: WrapperProps) =>
    props.type === TypographyTypes.display ? 'text-transform: uppercase;' : null;
const textAlign = (props: WrapperProps) => (props.align ? `text-align: ${props.align};` : null);

const TypographyStyling = (props: WrapperProps) => css`
    color: ${props.color};
    font-family: 'Alliance No.1';
    font-size: ${props.size}px;
    font-style: normal;
    font-weight: ${fontWeight(props)};
    letter-spacing: ${letterSpacing(props)};
    line-height: ${lineHeight(props)};
    ${gutterBottomSpacing(props)}
    ${fadedText(props)}
    ${showEllipsis(props)}
    ${textAlign(props)}
    ${textTransform(props)}
`;

export const Wrapper = styled.div`
    ${TypographyStyling}
    ${(props: WrapperProps) =>
        props.applyFontStylingOnInnerEl &&
        css`
            display: flex;

            & > * {
                ${TypographyStyling(props)}
            }
        `}
`;
